import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import {
  DocumentList,
  DocumentViewer,
  FolderSuggestionsCard,
  KeyTermsPanel,
  PropertySummaryCard,
  RightRail,
} from './components';
import { Participants } from './components/types';
import { useAgentComplianceReview } from './hooks/useAgentComplianceReview';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewDesktopProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * Desktop three-pane layout for the agent compliance review page.
 *
 * - **Left rail**: property summary, the key-terms panel, the folder-level
 *   suggestions card, and the selectable document list.
 * - **Center**: the {@link DocumentViewer} for the selected document (its own
 *   "select a document" empty state shows when nothing is selected).
 * - **Right rail**: document-level actions, suggestions, checklist mismatches,
 *   and passing checks.
 *
 * All behavior (detail load, derived validation, mutations, URL-driven
 * selection) comes from {@link useAgentComplianceReview}, shared with the
 * mobile single-pane layout. The right rail is hidden below `xl`; the mobile
 * layout (selected by breakpoint in the page shell) takes over there.
 */
export const AgentComplianceReviewDesktop: FC<
  AgentComplianceReviewDesktopProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const {
    document,
    loading,
    error,
    keyTerms,
    platformUrl,
    activeFieldId,
    validation,
    hasSelection,
    selectedDocSummary,
    selectedDocNeedsReview,
    platformRequirementIssues,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    onSelectFields,
    onSelectDocument,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  } = useAgentComplianceReview({
    overview,
    selectedDocumentId,
    token,
    ingestionFolderUuid,
  });

  return (
    <Grid
      bg="compliance.background.primary"
      gap={0}
      h="100dvh"
      maxH="100dvh"
      // Single full-height row that can shrink below its content (minmax 0)
      // so each column scrolls independently instead of growing the page.
      overflow="hidden"
      templateColumns={{ base: '280px 1fr', xl: '320px 1fr 340px' }}
      templateRows="minmax(0, 1fr)"
      w="full"
    >
      {/* Left rail */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderRightColor="compliance.sidebar.border"
        borderRightWidth={1}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <Box>
            <Text
              color="compliance.text.muted"
              fontSize="xs"
              fontWeight="medium"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Compliance review
            </Text>
            <Heading
              color="compliance.text.primary"
              fontSize="lg"
              mt={1}
              size="headline"
            >
              {overview.folderName}
            </Heading>
          </Box>

          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <KeyTermsPanel
            keyTerms={keyTerms}
            participants={
              overview.participants as Participants | null | undefined
            }
          />

          <FolderSuggestionsCard
            isIgnoring={isIgnoring}
            suggestions={overview.folderSuggestions}
            onIgnore={onIgnoreFolderSuggestion}
            onUndoIgnore={onUndoIgnoreFolderSuggestion}
          />

          <DocumentList
            documents={overview.documents}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </GridItem>

      {/* Center pane: PDF viewer */}
      <GridItem h="full" minH={0} minWidth={0} overflow="hidden">
        {error ? (
          <Flex
            align="center"
            bg="compliance.pdf.bg"
            h="full"
            justify="center"
            p={6}
            w="full"
          >
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document. Please try again.
            </Text>
          </Flex>
        ) : (
          <DocumentViewer
            activeFieldId={activeFieldId}
            documentName={document?.documentName}
            loading={loading}
            pdfUrl={document?.pdfUrl}
            submittedAt={
              document?.submittedAt != null
                ? String(document.submittedAt)
                : undefined
            }
            successFieldIds={validation.successFieldIds}
            validationFieldsMap={validation.validationFieldsMap}
            warningFieldIds={validation.errorFieldIds}
            warningFieldMessages={validation.fieldMessages}
          />
        )}
      </GridItem>

      {/* Right rail: suggestions + checks for the selected document */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderLeftColor="compliance.sidebar.border"
        borderLeftWidth={1}
        display={{ base: 'none', xl: 'block' }}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <Box p={4}>
          {error ? (
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document.
            </Text>
          ) : (
            <RightRail
              defaultResubmitMessage={defaultResubmitMessage}
              hasDetail={document != null}
              hasSelection={hasSelection}
              isIgnoring={isIgnoring}
              isIgnoringPlatformRequirementIssue={isIgnoringPlatformIssue}
              isResubmitted={selectedDocSummary?.agentResubmittedAt != null}
              isSubmitting={isSubmitting}
              loading={loading}
              needsReview={selectedDocNeedsReview}
              platformRequirementIssues={platformRequirementIssues}
              platformUrl={platformUrl}
              successfulChecks={validation.successfulChecks}
              suggestions={validation.suggestions}
              onIgnorePlatformRequirementIssue={
                onIgnorePlatformRequirementIssue
              }
              onIgnoreSuggestion={onIgnoreDocumentSuggestion}
              onResubmit={onResubmit}
              onSelectFields={onSelectFields}
              onUndoIgnorePlatformRequirementIssue={
                onUndoIgnorePlatformRequirementIssue
              }
              onUndoIgnoreSuggestion={onUndoIgnoreDocumentSuggestion}
            />
          )}
        </Box>
      </GridItem>
    </Grid>
  );
};
